// must stay the first import: configures zod before any module parses with it
import "./zodConfig";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App";
import { checkOnResume, deferredReload } from "./platform/pwaUpdate";
import { inSession } from "./platform/sessionPhase";
import { installClientLog } from "./services/clientLog";
import { installErrorReporting } from "./services/errorReporting";
import { initFeatureFlags } from "./services/featureFlags";
import { installSessionSummary } from "./services/sessionSummary";
// Self-hosted fonts (latin subset, font-display: swap): no third-party font host, so the CSP needs
// no font/style exception. Used by the site and the game HUD (--font-display / --font-body).
import "@fontsource/alegreya-sans/latin-400.css";
import "@fontsource/alegreya-sans/latin-400-italic.css";
import "@fontsource/alegreya-sans/latin-500.css";
import "@fontsource/alegreya-sans/latin-500-italic.css";
import "@fontsource/alegreya-sans/latin-700.css";
import "@fontsource/alegreya-sans/latin-800.css";
import "@fontsource/cinzel/latin-600.css";
import "@fontsource/cinzel/latin-700.css";
import "@fontsource/cinzel/latin-800.css";
import "./styles.css";

installClientLog();
installErrorReporting();
installSessionSummary();
// the server's flags (FLAG_OVERRIDES) and this device's debug overrides over the data defaults
void initFeatureFlags();
// a new deploy reloads the page only when no one is mid-session (platform/pwaUpdate.ts)
registerSW({
  immediate: true,
  onNeedReload: deferredReload(inSession),
  // a home-screen app resumed from the background never navigates: look for a new deploy then
  onRegisteredSW: (_url, registration) => {
    if (!registration) return;
    checkOnResume({
      onVisible: (fn) =>
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") fn();
        }),
      now: () => Date.now(),
      check: () => void registration.update().catch(() => undefined),
      minGapMs: 60_000,
    });
  },
});

const root = document.getElementById("app");
if (!root) throw new Error("#app root missing");
createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
